import { ArrowRight, Check, ChevronRight, Info, ListChecks, Search, ShieldCheck } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import PublicHeader from "@/components/PublicHeader";

export { PublicHeader };
import NotFound from "@/components/NotFound";
import { useHeadMeta } from "@/lib/head";
import { SITE, SITE_URL } from "@/content/site";
import { USE_CASES, USE_CASES_HUB, findUseCase, pathForUseCase } from "@/content/useCases.mjs";
import "./HomepageFonts.css";
import "./Homepage.css";
import "./UseCases.css";

export const jsonLd = graph => ({ __html: JSON.stringify(graph).replace(/</g, "\\u003c") });

export function breadcrumbs(items) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({ "@type": "ListItem", position: i + 1, name: item.name, item: `${SITE_URL}${item.path}` })),
  };
}

export function Crumbs({ items }) {
  return <nav aria-label="Breadcrumb" className="ss-uc-crumbs"><ol>
    {items.map((item, i) => <li key={item.path}>{i < items.length - 1 ? <><a href={item.path}>{item.name}</a><ChevronRight size={14} aria-hidden="true" /></> : <span aria-current="page">{item.name}</span>}</li>)}
  </ol></nav>;
}

export function ClosingBand() {
  return <section className="ss-uc-close" aria-labelledby="uc-close-title"><div className="ss-container">
    <h2 id="uc-close-title">See your own priorities</h2>
    <p>Connect Search Console and Google Analytics with read-only access. Your first priorities are free, and the demo needs no account.</p>
    <div className="ss-actions"><a className="ss-button ss-primary" href="/">Get started free <ArrowRight size={16} aria-hidden="true" /></a><a className="ss-button ss-ghost" href="/#how">How it works</a></div>
  </div></section>;
}

const HOME_CRUMB = { name: "Home", path: "/" };
const HUB_CRUMB = { name: "Use cases", path: USE_CASES_HUB.path };

export function UseCasesIndex() {
  useHeadMeta({ title: USE_CASES_HUB.title, robots: "index, follow" });
  const graph = { "@context": "https://schema.org", "@graph": [
    { "@type": "CollectionPage", "@id": `${SITE_URL}${USE_CASES_HUB.path}#webpage`, url: `${SITE_URL}${USE_CASES_HUB.path}`, name: USE_CASES_HUB.title, description: USE_CASES_HUB.description, inLanguage: "en-GB", isPartOf: { "@id": `${SITE_URL}/#website` }, publisher: { "@id": `${SITE_URL}/#organization` },
      hasPart: USE_CASES.map(u => ({ "@type": "WebPage", url: `${SITE_URL}${pathForUseCase(u.slug)}`, name: u.h1 })) },
    breadcrumbs([HOME_CRUMB, HUB_CRUMB]),
  ] };
  return <div className="ss-home ss-uc">
    <script type="application/ld+json" dangerouslySetInnerHTML={jsonLd(graph)} />
    <a className="ss-skip" href="#main">Skip to content</a>
    <PublicHeader />
    <main id="main">
      <section className="ss-uc-hero"><div className="ss-container">
        <Crumbs items={[HOME_CRUMB, HUB_CRUMB]} />
        <p className="ss-eyebrow">{USE_CASES_HUB.eyebrow}</p>
        <h1>{USE_CASES_HUB.h1}</h1>
        <p className="ss-lead">{USE_CASES_HUB.lead}</p>
      </div></section>
      <section className="ss-container ss-uc-grid-wrap" aria-label="Use cases">
        <ul className="ss-uc-grid">{USE_CASES.map(u => <li key={u.slug}><a href={pathForUseCase(u.slug)} className="ss-uc-card" data-testid={`use-case-link-${u.slug}`}>
          <span className="ss-uc-card-eyebrow">{u.eyebrow}</span>
          <h2>{u.h1}</h2>
          <p>{u.description}</p>
          <span className="ss-uc-card-more">Read more <ArrowRight size={16} aria-hidden="true" /></span>
        </a></li>)}</ul>
      </section>
      <ClosingBand />
    </main>
    <SiteFooter />
  </div>;
}

export default function UseCasePage({ slug }) {
  const page = findUseCase(slug);
  useHeadMeta({ title: page?.title, robots: page ? "index, follow" : "noindex, nofollow" });
  if (!page) return <NotFound />;
  const path = pathForUseCase(page.slug);
  const crumbs = [HOME_CRUMB, HUB_CRUMB, { name: page.nav, path }];
  const graph = { "@context": "https://schema.org", "@graph": [
    { "@type": "WebPage", "@id": `${SITE_URL}${path}#webpage`, url: `${SITE_URL}${path}`, name: page.title, description: page.description, inLanguage: "en-GB", isPartOf: { "@id": `${SITE_URL}/#website` }, about: { "@id": `${SITE_URL}/#software` }, publisher: { "@id": `${SITE_URL}/#organization` } },
    breadcrumbs(crumbs),
  ] };
  const related = page.related.map(findUseCase).filter(Boolean);
  return <div className="ss-home ss-uc">
    <script type="application/ld+json" dangerouslySetInnerHTML={jsonLd(graph)} />
    <a className="ss-skip" href="#main">Skip to content</a>
    <PublicHeader />
    <main id="main">
      <section className="ss-uc-hero"><div className="ss-container ss-uc-hero-grid">
        <div>
          <Crumbs items={crumbs} />
          <p className="ss-eyebrow">{page.eyebrow}</p>
          <h1>{page.h1}</h1>
          <p className="ss-lead">{page.lead}</p>
          <div className="ss-actions"><a className="ss-button ss-primary" href="/">Get started free <ArrowRight size={16} aria-hidden="true" /></a><a className="ss-button ss-secondary" href="/#how">How it works</a></div>
          <ul className="ss-uc-ticks"><li><ShieldCheck size={16} aria-hidden="true" />Read-only Google access</li><li><Check size={16} aria-hidden="true" />Search Console and GA4 together</li><li><Check size={16} aria-hidden="true" />{SITE.name} never edits your site</li></ul>
        </div>
        <figure className="ss-uc-example" data-testid="use-case-example">
          <figcaption><span>Example</span>{page.example.label}</figcaption>
          <p className="ss-uc-example-site">{page.example.site}</p>
          <dl>{page.example.rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
          <p className="ss-uc-example-readout">{page.example.readout}</p>
          <p className="ss-uc-example-note">Sample data for illustration, not a customer result.</p>
        </figure>
      </div></section>

      <section className="ss-container ss-section ss-uc-problem" aria-labelledby="uc-problem">
        <h2 id="uc-problem">{page.problem.heading}</h2>
        <p>{page.problem.body}</p>
      </section>

      <section className="ss-uc-band" aria-labelledby="uc-looks"><div className="ss-container ss-uc-two">
        <div className="ss-uc-panel">
          <span className="ss-uc-icon"><Search size={22} aria-hidden="true" /></span>
          <h2 id="uc-looks">What {SITE.name} looks for</h2>
          <ul className="ss-uc-list">{page.looksFor.map(item => <li key={item}><Check size={18} aria-hidden="true" />{item}</li>)}</ul>
        </div>
        <div className="ss-uc-panel">
          <span className="ss-uc-icon"><ListChecks size={22} aria-hidden="true" /></span>
          <h2>What to do next</h2>
          <ol className="ss-uc-steps">{page.steps.map((step, i) => <li key={step}><span>{String(i + 1).padStart(2, "0")}</span>{step}</li>)}</ol>
        </div>
      </div></section>

      <section className="ss-container ss-section ss-uc-limits" aria-labelledby="uc-limits">
        <span className="ss-uc-icon ss-uc-icon-muted"><Info size={22} aria-hidden="true" /></span>
        <h2 id="uc-limits">Limits worth knowing</h2>
        <ul className="ss-uc-list ss-uc-list-muted">{page.limits.map(item => <li key={item}><Info size={18} aria-hidden="true" />{item}</li>)}</ul>
      </section>

      <section className="ss-section ss-surface" aria-labelledby="uc-faq"><div className="ss-container ss-faq">
        <h2 id="uc-faq">Questions</h2>
        <div>{page.faqs.map(item => <details key={item.q}><summary>{item.q}</summary><p>{item.a}</p></details>)}</div>
      </div></section>

      <section className="ss-container ss-section ss-uc-related" aria-labelledby="uc-related">
        <h2 id="uc-related">Related use cases</h2>
        <ul className="ss-uc-grid ss-uc-grid-small">{related.map(u => <li key={u.slug}><a href={pathForUseCase(u.slug)} className="ss-uc-card">
          <span className="ss-uc-card-eyebrow">{u.eyebrow}</span>
          <h3>{u.h1}</h3>
          <span className="ss-uc-card-more">Read more <ArrowRight size={16} aria-hidden="true" /></span>
        </a></li>)}</ul>
        <p><a href={USE_CASES_HUB.path}>All use cases</a></p>
      </section>
      <ClosingBand />
    </main>
    <SiteFooter />
  </div>;
}
